<script setup>
// Marcas dos canais em que o agente atende. O WhatsApp é a marca real, minimalista, em SVG
// inline — não um chip escrito "WA", que exige decodificar uma sigla para saber onde o agente
// responde. `web` já entra aqui porque o widget embedado chega em breve.
//
// Sem canal nenhum, o componente NÃO fica vazio: mostra um alerta. Um agente publicado e
// desconectado é indistinguível de um agente saudável até alguém reclamar que ele não responde.
import { useI18n } from 'vue-i18n';

// Não há filtro de canais conhecidos: o template marca os que sabe desenhar (inclusive o
// `google_chat`, 30/09/2026) e ignora o resto. O `conhecidos` que filtrava aqui não era lido
// por ninguém, e o lint barrava qualquer commit neste arquivo por isso.
defineProps({
  channels: { type: Array, default: () => [] },
});

const { t } = useI18n();
</script>

<template>
  <div class="flex items-center gap-1.5">
    <template v-if="channels.length">
      <span
        v-if="channels.includes('whatsapp')"
        :title="t('POWER_AGENTS.CARD.CHANNEL_WHATSAPP')"
        class="inline-flex"
      >
        <svg
          viewBox="0 0 24 24"
          class="size-4 fill-[#25D366]"
          aria-hidden="true"
        >
          <path
            d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.3-1.38a9.86 9.86 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.44 9.9-9.9 0-2.64-1.03-5.13-2.9-7A9.82 9.82 0 0 0 12.04 2Zm5.8 14.13c-.24.68-1.42 1.31-1.96 1.36-.5.05-.98.24-3.32-.7-2.79-1.1-4.56-3.95-4.7-4.13-.13-.19-1.12-1.49-1.12-2.84 0-1.35.71-2.02.96-2.29.25-.27.55-.34.73-.34h.52c.17 0 .4-.06.62.48.24.57.8 1.97.87 2.11.07.14.12.3.02.49-.1.19-.15.3-.29.47-.15.17-.3.38-.43.5-.14.14-.29.3-.13.58.17.29.74 1.22 1.59 1.98 1.09.97 2.01 1.27 2.29 1.42.29.14.45.12.62-.07.17-.19.71-.83.9-1.12.19-.29.38-.24.63-.14.26.09 1.65.78 1.93.92.29.14.48.21.55.33.07.12.07.7-.17 1.38Z"
          />
        </svg>
      </span>
      <span
        v-if="channels.includes('web_widget')"
        :title="t('POWER_AGENTS.CARD.CHANNEL_WEB')"
        class="inline-flex"
      >
        <i class="i-lucide-globe size-4 text-n-slate-11" />
      </span>
      <span
        v-if="channels.includes('api')"
        :title="t('POWER_AGENTS.CARD.CHANNEL_API')"
        class="inline-flex"
      >
        <i class="i-lucide-plug size-4 text-n-slate-11" />
      </span>
      <!-- O servidor manda o tipo como `demodulize.underscore`: Channel::GoogleChat → google_chat. -->
      <span
        v-if="channels.includes('google_chat')"
        :title="t('POWER_AGENTS.CARD.CHANNEL_GOOGLE_CHAT')"
        class="inline-flex"
      >
        <i class="i-woot-google-chat size-4 text-n-slate-11" />
      </span>
    </template>

    <!-- Sem inbox: alerta com o motivo no hover. É a diferença entre "não responde porque
         ninguém ligou" e "não responde por bug" — e a segunda leitura custa horas. -->
    <span
      v-else
      :title="t('POWER_AGENTS.CARD.NO_INBOX')"
      class="inline-flex items-center gap-1 text-n-amber-11"
    >
      <i class="i-lucide-triangle-alert size-4" />
    </span>
  </div>
</template>
